import React, { useState, useEffect } from 'react';
import { History, Search, RefreshCw, Plus, Pencil, Trash2, RotateCcw, Gem, Sparkles, ArrowUpCircle, Trophy, BookOpen, FileText, Users, MapPin, Crown } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import api from '../../lib/api';

// Kategorie-Konfiguration für farbige Tags
const CATEGORY_CONFIG = {
    relic: { icon: Gem, color: 'text-cyan-400', bg: 'bg-cyan-500/20', border: 'border-cyan-500/30', label: 'Relic' },
    relics: { icon: Gem, color: 'text-cyan-400', bg: 'bg-cyan-500/20', border: 'border-cyan-500/30', label: 'Relic' },
    accessory: { icon: Crown, color: 'text-yellow-400', bg: 'bg-yellow-500/20', border: 'border-yellow-500/30', label: 'Accessory' },
    accessories: { icon: Crown, color: 'text-yellow-400', bg: 'bg-yellow-500/20', border: 'border-yellow-500/30', label: 'Accessory' },
    gacha: { icon: Sparkles, color: 'text-pink-400', bg: 'bg-pink-500/20', border: 'border-pink-500/30', label: 'Gacha' },
    gachas: { icon: Sparkles, color: 'text-pink-400', bg: 'bg-pink-500/20', border: 'border-pink-500/30', label: 'Gacha' },
    upgrade: { icon: ArrowUpCircle, color: 'text-blue-400', bg: 'bg-blue-500/20', border: 'border-blue-500/30', label: 'Upgrade' },
    upgrades: { icon: ArrowUpCircle, color: 'text-blue-400', bg: 'bg-blue-500/20', border: 'border-blue-500/30', label: 'Upgrade' },
    achievement: { icon: Trophy, color: 'text-amber-400', bg: 'bg-amber-500/20', border: 'border-amber-500/30', label: 'Achievement' },
    achievements: { icon: Trophy, color: 'text-amber-400', bg: 'bg-amber-500/20', border: 'border-amber-500/30', label: 'Achievement' },
    tutorial: { icon: BookOpen, color: 'text-purple-400', bg: 'bg-purple-500/20', border: 'border-purple-500/30', label: 'Tutorial' },
    tutorials: { icon: BookOpen, color: 'text-purple-400', bg: 'bg-purple-500/20', border: 'border-purple-500/30', label: 'Tutorial' },
    island: { icon: MapPin, color: 'text-green-400', bg: 'bg-green-500/20', border: 'border-green-500/30', label: 'Insel' },
    gacha_island: { icon: MapPin, color: 'text-green-400', bg: 'bg-green-500/20', border: 'border-green-500/30', label: 'Gacha Insel' },
    upgrade_island: { icon: MapPin, color: 'text-green-400', bg: 'bg-green-500/20', border: 'border-green-500/30', label: 'Upgrade Insel' },
    patch_note: { icon: FileText, color: 'text-orange-400', bg: 'bg-orange-500/20', border: 'border-orange-500/30', label: 'Patch Note' },
    team_member: { icon: Users, color: 'text-violet-400', bg: 'bg-violet-500/20', border: 'border-violet-500/30', label: 'Team' },
    meta_build: { icon: Crown, color: 'text-rose-400', bg: 'bg-rose-500/20', border: 'border-rose-500/30', label: 'Meta Build' },
    entry: { icon: Gem, color: 'text-cyan-400', bg: 'bg-cyan-500/20', border: 'border-cyan-500/30', label: 'Eintrag' },
};

export const ChangeHistoryManager = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    
    // Filters
    const [searchUsername, setSearchUsername] = useState('');
    const [searchAction, setSearchAction] = useState('');
    const [searchEntityType, setSearchEntityType] = useState('');
    const [searchDateFrom, setSearchDateFrom] = useState('');
    const [searchDateTo, setSearchDateTo] = useState('');
    
    // Pagination
    const [page, setPage] = useState(0);
    const limit = 50;

    useEffect(() => {
        loadLogs();
    }, [page]);

    const loadLogs = async () => {
        setLoading(true);
        try {
            const params = {
                limit,
                skip: page * limit,
            };
            if (searchUsername) params.username = searchUsername;
            if (searchAction) params.action = searchAction;
            if (searchEntityType) params.entity_type = searchEntityType;
            if (searchDateFrom) params.date_from = searchDateFrom;
            if (searchDateTo) params.date_to = searchDateTo;
            
            const response = await api.get('/activity-logs', { params });
            setLogs(response.data.logs || []);
            setTotal(response.data.total || 0);
        } catch (error) {
            console.error('Failed to load activity logs:', error);
            toast.error('Fehler beim Laden der Änderungshistorie');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        setPage(0);
        loadLogs();
    };

    const handleReset = () => {
        setSearchUsername('');
        setSearchAction('');
        setSearchEntityType('');
        setSearchDateFrom('');
        setSearchDateTo('');
        setPage(0);
        setTimeout(loadLogs, 100);
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const getActionIcon = (action) => {
        switch (action) {
            case 'create':
                return <Plus size={14} className="text-green-400" />;
            case 'update':
                return <Pencil size={14} className="text-blue-400" />;
            case 'delete':
                return <Trash2 size={14} className="text-red-400" />;
            case 'restore':
                return <RotateCcw size={14} className="text-amber-400" />;
            default:
                return <History size={14} className="text-gray-400" />;
        }
    };

    const getActionBadge = (action) => {
        const styles = {
            create: 'bg-green-500/20 text-green-400 border-green-500/30',
            update: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
            delete: 'bg-red-500/20 text-red-400 border-red-500/30',
            restore: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
            export: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
            import: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
        };
        const labels = {
            create: 'Erstellt',
            update: 'Bearbeitet',
            delete: 'Gelöscht',
            restore: 'Wiederhergestellt',
            export: 'Exportiert',
            import: 'Importiert',
        };
        return (
            <span className={cn(
                "px-2 py-0.5 rounded text-xs font-medium border",
                styles[action] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'
            )}>
                {labels[action] || action}
            </span>
        );
    };

    const getEntityTypeBadge = (entityType) => {
        const config = CATEGORY_CONFIG[entityType] || {
            icon: History,
            color: 'text-gray-400',
            bg: 'bg-gray-500/20',
            border: 'border-gray-500/30',
            label: entityType
        };
        const Icon = config.icon;
        
        return (
            <span className={cn(
                "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border",
                config.bg,
                config.color,
                config.border
            )}>
                <Icon size={12} />
                {config.label}
            </span>
        );
    };

    const formatDetails = (details) => {
        if (!details) return null;
        
        const entries = Object.entries(details).filter(([key, value]) => 
            value !== null && value !== undefined && key !== 'id'
        );
        
        if (entries.length === 0) return null;
        
        return (
            <div className="mt-2 p-2 rounded bg-black/30 text-xs space-y-1">
                {entries.map(([key, value]) => (
                    <div key={key} className="flex gap-2">
                        <span className="text-gray-500 font-mono">{key}:</span>
                        <span className="text-gray-300 truncate">
                            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                        </span>
                    </div>
                ))}
            </div>
        );
    };

    const totalPages = Math.ceil(total / limit);

    return (
        <div className="space-y-6" data-testid="change-history-manager">
            {/* Search & Filters */}
            <div className="p-6 rounded-xl bg-card border border-white/10">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
                    <Search size={18} className="text-cyan-400" />
                    Filter & Suche
                </h3>
                
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                    <div>
                        <Label className="text-gray-400 text-xs">Admin Name</Label>
                        <Input
                            data-testid="filter-username"
                            value={searchUsername}
                            onChange={(e) => setSearchUsername(e.target.value)}
                            placeholder="Name..."
                            className="bg-black/40 border-white/10"
                        />
                    </div>
                    <div>
                        <Label className="text-gray-400 text-xs">Aktion</Label>
                        <select
                            data-testid="filter-action"
                            value={searchAction}
                            onChange={(e) => setSearchAction(e.target.value)}
                            className="w-full p-2 rounded-lg bg-black/40 border border-white/10 text-white text-sm"
                        >
                            <option value="">Alle</option>
                            <option value="create">Erstellt</option>
                            <option value="update">Bearbeitet</option>
                            <option value="delete">Gelöscht</option>
                            <option value="restore">Wiederhergestellt</option>
                        </select>
                    </div>
                    <div>
                        <Label className="text-gray-400 text-xs">Typ</Label>
                        <select
                            data-testid="filter-entity-type"
                            value={searchEntityType}
                            onChange={(e) => setSearchEntityType(e.target.value)}
                            className="w-full p-2 rounded-lg bg-black/40 border border-white/10 text-white text-sm"
                        >
                            <option value="">Alle</option>
                            <option value="relics">Relics</option>
                            <option value="accessories">Accessories</option>
                            <option value="tutorials">Tutorials</option>
                            <option value="gacha">Gacha</option>
                            <option value="upgrade">Upgrade</option>
                            <option value="achievement">Achievement</option>
                            <option value="gacha_island">Gacha Insel</option>
                            <option value="upgrade_island">Upgrade Insel</option>
                            <option value="patch_note">Patch Notes</option>
                            <option value="team_member">Team</option>
                            <option value="entry">Eintrag (alt)</option>
                        </select>
                    </div>
                    <div>
                        <Label className="text-gray-400 text-xs">Von</Label>
                        <Input
                            data-testid="filter-date-from"
                            type="date"
                            value={searchDateFrom}
                            onChange={(e) => setSearchDateFrom(e.target.value)}
                            className="bg-black/40 border-white/10"
                        />
                    </div>
                    <div>
                        <Label className="text-gray-400 text-xs">Bis</Label>
                        <Input
                            data-testid="filter-date-to"
                            type="date"
                            value={searchDateTo}
                            onChange={(e) => setSearchDateTo(e.target.value)}
                            className="bg-black/40 border-white/10"
                        />
                    </div>
                </div>
                
                <div className="flex items-center gap-3">
                    <Button
                        onClick={handleSearch}
                        className="bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 border border-cyan-500/30"
                        data-testid="search-button"
                    >
                        <Search size={16} className="mr-2" />
                        Suchen
                    </Button>
                    <Button
                        onClick={handleReset}
                        variant="ghost"
                        className="text-gray-400 hover:text-white"
                    >
                        <RefreshCw size={16} className="mr-2" />
                        Zurücksetzen
                    </Button>
                    <span className="text-sm text-gray-500 ml-auto">
                        {total} Einträge gefunden
                    </span>
                </div>
            </div>

            {/* Activity Log List */}
            <div className="p-6 rounded-xl bg-card border border-white/10">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                        <History size={18} className="text-purple-400" />
                        Änderungshistorie
                    </h3>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={loadLogs}
                        className="text-gray-400 hover:text-white"
                    >
                        <RefreshCw size={14} />
                    </Button>
                </div>
                
                {loading ? (
                    <div className="flex justify-center py-12">
                        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : logs.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                        Keine Änderungen gefunden
                    </div>
                ) : (
                    <div className="space-y-3">
                        {logs.map((log, index) => (
                            <div
                                key={log.id || index}
                                className="p-4 rounded-lg bg-white/5 border border-white/10 hover:border-white/20 transition-colors"
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex items-start gap-3 flex-1 min-w-0">
                                        <div className="mt-1">
                                            {getActionIcon(log.action)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                                <span className="font-medium text-white">
                                                    {log.username}
                                                </span>
                                                {getActionBadge(log.action)}
                                                {getEntityTypeBadge(log.entity_type)}
                                            </div>
                                            <p className="text-sm text-gray-400 truncate">
                                                {log.entity_title || log.entity_id}
                                            </p>
                                            {formatDetails(log.details)}
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p className="text-xs text-gray-500 font-mono">
                                            {formatDate(log.created_at)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-6">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage(p => Math.max(0, p - 1))}
                            disabled={page === 0}
                            className="border-white/10"
                        >
                            Zurück
                        </Button>
                        <span className="text-sm text-gray-400 px-4">
                            Seite {page + 1} von {totalPages}
                        </span>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                            disabled={page >= totalPages - 1}
                            className="border-white/10"
                        >
                            Weiter
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};
